import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CaseListingProps } from './CaseListing.data';
import styles from './CaseListing.module.scss';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import Heading from '../../atoms/heading/Heading';
import DraggableFilterButtons from '../../molecules/draggable-filter-buttons/DraggableFilterButtons';
import PrimaryDropdown from '../../atoms/primary-dropdown/PrimaryDropdown';
import WorkFilterVisuals from '../../molecules/work-filter-visuals/WorkFilterVisuals';
import type { WorkFilterVisualsRef } from '../../molecules/work-filter-visuals/WorkFilterVisuals.data';
import { filterCases, filterParams, getLocationFilters, useCaseFilter } from './useCaseFilter';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { fadeFromTo, fadeTo } from '../../../util/animation/fade/fade';
import { dataSourceToUrlQuery } from '../../../util/url.utils';
import { expandSpacing } from './CaseListing.transitions';
import { useTimeout } from '../../../hooks/useTimeout';
import { useGlobalStore } from '../../../stores/global';
import type { Card } from '../../../util/storyblok/types/global.types';
import CaseCard from 'components/blocks/case-card/CaseCard';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

const CASES_PER_PAGE = 12;

export default function CaseListing({ className, blok, ref }: CaseListingProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, true);
  const visualsRef = useRef<WorkFilterVisualsRef>(null);

  const store = useGlobalStore();
  const { allCases, countryCode } = store;

  const geoFilterCards = useMemo(() => {
    let filteredCards: Array<Card> = [];
    if (countryCode) {
      filteredCards = blok.cards?.filter((cardItem) => {
        if (typeof cardItem.case === 'string') {
          // if the resolve_relations is not sorted
          return true;
        }
        if (cardItem.case.content.countries?.length > 0) {
          if (cardItem.case.content.exclude_countries) {
            return !cardItem.case.content.countries?.includes(countryCode);
          } // else
          return cardItem.case.content.countries?.includes(countryCode);
        }
        return true;
      });
    } else {
      return blok.cards;
    }

    return filteredCards;
  }, [blok.cards, countryCode]);

  const { cases, activeCategoryFilter, onFilterClick, activeLocationFilter } = useCaseFilter(
    {
      ...blok,
      cards: geoFilterCards,
    },
    allCases || [],
  );

  const [renderedCases, setRenderedCases] = useState(cases);
  const [visibleCasesCount, setVisibleCasesCount] = useState(CASES_PER_PAGE);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const filterSpacingRef = useRef<HTMLDivElement>(null);

  const cardsWithUuid = useMemo(() => renderedCases?.filter((c) => c.uuid) ?? [], [renderedCases]);
  const visibleCases = useMemo(
    () => cardsWithUuid.slice(0, visibleCasesCount),
    [cardsWithUuid, visibleCasesCount],
  );
  const hasMoreCases = visibleCasesCount < cardsWithUuid.length;

  useUpdateEffect(() => {
    (async () => {
      if (listContainerRef?.current) {
        await fadeTo(listContainerRef.current, { duration: 0.2, to: 0 });
        setRenderedCases(cases);
        setVisibleCasesCount(CASES_PER_PAGE);
        fadeFromTo(listContainerRef.current, { duration: 0.2, to: 1, from: 0 });
      }
    })();
  }, [cases]);

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [renderedCases, visibleCasesCount]);

  const { start: refreshScrollTriggerTimeout } = useTimeout(
    () => {
      ScrollTrigger.refresh();
    },
    0,
    false,
  );

  useEffect(
    () => {
      if (activeCategoryFilter !== blok.category_all) {
        // space to 0
        expandSpacing(filterSpacingRef, false, refreshScrollTriggerTimeout);
      } else {
        // space to auto
        expandSpacing(filterSpacingRef, true, refreshScrollTriggerTimeout);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCategoryFilter, blok.category_all],
  );

  const categoryFiltersButtons = useMemo(() => {
    const icons = ['changes-society', 'creates-culture', 'invents-the-future'];
    let filters = [{ filter: blok.category_all }];
    const categories = blok.category_filters?.map((item) => {
      if (icons.includes(dataSourceToUrlQuery(item))) {
        return {
          icon: dataSourceToUrlQuery(item),
          filter: item,
        };
      }
      return {
        filter: item,
      };
    });

    filters = filters.concat(categories as Array<{ filter: string; icon?: string }>);

    return filters as Array<{ filter: string; icon?: string }>;
  }, [blok.category_all, blok.category_filters]);

  const countriesList = useMemo(() => {
    const list = new Map<string, string>();
    getLocationFilters(blok.locations_filter).forEach(({ value, label }) => {
      try {
        if (
          allCases?.some((card) =>
            card.content?.locations?.some((caseLoc) => filterCases(caseLoc, value)),
          )
        ) {
          list.set(value, label);
        }
      } catch (error) {
        console.error(error);
        list.set(value, label);
      }
    });
    return [...list].sort(([, a], [, b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [blok.locations_filter, allCases]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.caseListing, className, 'max-content-width')}
      {...sbEditable(blok)}
    >
      {blok.title && (
        <div className={classNames('grid-gutter')}>
          <Heading
            as="h2"
            size="heading-03"
            className={classNames(styles.title, styles.fullBleed)}
            disableTriggerInOnScroll={false}
          >
            {blok.title}
          </Heading>
        </div>
      )}

      {((blok.category_filters && blok.category_filters.length > 0) ||
        (blok.locations_filter && blok.locations_filter.length > 0)) && (
        <>
          <div className={classNames(styles.filters, 'grid-gutter')}>
            {blok.category_filters && blok.category_filters.length > 0 && blok.category_all && (
              <DraggableFilterButtons
                filters={categoryFiltersButtons}
                activeFilter={activeCategoryFilter}
                onFilterClick={onFilterClick}
                colorVariation="dark7"
                type={filterParams.category}
                className={classNames(styles.categoryFilter)}
              />
            )}

            {blok.locations_filter?.length && blok.location_all && (
              <PrimaryDropdown
                className={classNames(styles.locationFilter)}
                filters={[blok.location_all, ...countriesList.map(([value]) => value)]}
                filterLabels={Object.fromEntries(countriesList)}
                activeFilter={activeLocationFilter}
                type={filterParams.location}
                textVariant="button-label-large"
                onFilterClick={onFilterClick}
              />
            )}
          </div>

          <div ref={filterSpacingRef} className={classNames(styles.filterSpacingWrapper)}>
            <div className={classNames(styles.filterSpacing)} />
          </div>

          {blok.category_filters && blok.category_filters.length > 0 && (
            <WorkFilterVisuals
              ref={visualsRef}
              categories={blok.category_filters}
              activeCategory={activeCategoryFilter}
              className={classNames(styles.visuals)}
            />
          )}
        </>
      )}

      <div
        ref={listContainerRef}
        className={classNames(styles.listContainer, {
          'grid-row': !isMobileViewport,
        })}
      >
        {blok.highlighted_case?.[0] && (
          <CaseCard
            className={classNames(styles.cardItem, {
              [styles.fullBleed]: true,
            })}
            blok={{ case: blok.highlighted_case[0].case, fullBleed: true }}
          />
        )}

        {visibleCases.map((card) => {
          // const fullBleed = !isMobileViewport && !card.half_width;
          const fullBleed = false;
          return (
            <CaseCard
              className={classNames(styles.cardItem, {
                [styles.fullBleed]: fullBleed,
              })}
              blok={{ case: card, fullBleed }}
              key={card.uuid}
            />
          );
        })}
      </div>

      {hasMoreCases && (
        <div className={classNames(styles.showMoreWrapper)}>
          <PrimaryButton
            label={blok.more_label || 'Show more'}
            disableTriggerInOnScroll={false}
            onClick={() => setVisibleCasesCount((count) => count + CASES_PER_PAGE)}
          />
        </div>
      )}
    </div>
  );
}
